import type { AssemblyModel } from '@/types/assembly.types';
import type { AssemblyDrawingSpec } from '@/types/assemblyDrawing.types';
import type { ToolImportReport } from '@/types/toolImport.types';
import type { ToolSystem } from '@/types/toolSystem.types';
import { apiClient } from './client';

/** Image format the backend can render an assembly drawing in (backend `DrawingFormat`). */
export type DrawingFormat = 'svg' | 'png';

/** Options of one tool-database import run. */
export interface ImportXmlOptions {
  /**
   * Retire active export-origin assemblies that this export no longer lists.
   *
   * Omitted from the request when undefined, which leaves the backend default (`true`).
   */
  retireMissing?: boolean;
  /**
   * Tool system the export is imported into.
   *
   * Omitted from the request when undefined, which leaves the backend default
   * (`hypermill` — an omtdx export is by definition the hyperMILL catalog).
   */
  toolSystem?: ToolSystem;
}

/** Parsing an export takes seconds, not minutes — but well over a plain request. */
const IMPORT_TIMEOUT_MS = 120000;

/** Server-side filter of `GET /assemblies` (backend `AssemblyListFilter`, the subset the UI uses). */
export interface AssembliesListParams {
  active?: boolean;
  page?: number;
  size?: number;
  /** Only the assemblies of this catalog; machine numbers repeat across the two. */
  toolSystem?: ToolSystem | null;
}

/**
 * Page size of {@link fetchAllActiveAssemblies}.
 *
 * The backend declares no maximum — omitting `size` returns the whole table — but paging
 * keeps the helper correct if a cap is ever introduced. The catalogue holds under a
 * hundred active assemblies, so in practice this is a single request.
 */
const ALL_ASSEMBLIES_PAGE_SIZE = 500;

/** Stops the paging loop from running away if the backend ever ignores `size`. */
const ALL_ASSEMBLIES_MAX_PAGES = 50;

export const assembliesApi = {
  /** GET /assemblies?tool_id={toolId} - assemblies mounted on one tool (usually 0-2). */
  getByToolId: async (toolId: string): Promise<AssemblyModel[]> => {
    const searchParams = new URLSearchParams();
    searchParams.set('tool_id', toolId);
    const { data } = await apiClient.get<AssemblyModel[]>(`/assemblies?${searchParams.toString()}`);
    return data;
  },

  /**
   * GET /assemblies - the assembly list, each row with its nested tool, holder and extension.
   *
   * The route is cached server-side for 300 s and the cache is dropped after an XML
   * import, so a refetch right after an import does see the new rows.
   */
  getAll: async (filter?: AssembliesListParams): Promise<AssemblyModel[]> => {
    const searchParams = new URLSearchParams();
    if (filter?.active != null) searchParams.set('active', String(filter.active));
    if (filter?.page != null) searchParams.set('page', String(filter.page));
    if (filter?.size != null) searchParams.set('size', String(filter.size));
    if (filter?.toolSystem != null) searchParams.set('tool_system', filter.toolSystem);
    const query = searchParams.toString();
    const { data } = await apiClient.get<AssemblyModel[]>(query ? `/assemblies?${query}` : '/assemblies');
    return data;
  },

  /**
   * GET /assemblies/{id}/drawing-data - the drawing as data, not as a picture.
   *
   * Carries the body outlines, the dimensions and the property-panel rows with raw
   * numbers and no labels, so the dialog can draw the sheet itself as SVG and write the
   * words in the user's language.
   *
   * Rejects with 404 (unknown assembly) or 422 (assembly exists but cannot be drawn),
   * exactly like the image endpoint below.
   */
  getDrawingData: async (assemblyId: string): Promise<AssemblyDrawingSpec> => {
    const { data } = await apiClient.get<AssemblyDrawingSpec>(`/assemblies/${assemblyId}/drawing-data`);
    return data;
  },

  /**
   * GET /assemblies/{id}/drawing - the backend-rendered image of the same drawing.
   *
   * Still the source of the "download PNG" action: the dialog itself renders from
   * `getDrawingData`, but a file the user saves should be the paginated sheet the
   * backend composes, panel included.
   *
   * Returned as a Blob because a plain `<img src="/api/v1/assemblies/.../drawing">`
   * would 401 — images don't carry the app's Bearer token.
   *
   * Rejects with 404 (unknown assembly) or 422 (assembly exists but cannot be drawn).
   */
  getDrawing: async (assemblyId: string, fmt: DrawingFormat = 'svg'): Promise<Blob> => {
    const searchParams = new URLSearchParams({ fmt });
    const { data } = await apiClient.get<Blob>(`/assemblies/${assemblyId}/drawing?${searchParams.toString()}`, {
      responseType: 'blob',
    });
    return data;
  },

  /**
   * POST /assemblies/load-xml - import a hyperMILL tool-database export (.xml / .omtdx).
   *
   * One multipart file fills the whole catalogue in a single run: materials, holders,
   * extensions, tools, cutting data and assemblies. The returned report is what the run
   * actually wrote, plus the findings worth showing the user (skipped tools, unreferenced
   * holders, warnings).
   *
   * Rejects with 400 (`detail` explains what the parser choked on), 409 (conflicting
   * catalogue state) or the usual auth / server statuses.
   */
  importXml: async (file: File, options?: ImportXmlOptions): Promise<ToolImportReport> => {
    const formData = new FormData();
    formData.append('file', file);

    const searchParams = new URLSearchParams();
    if (options?.retireMissing != null) searchParams.set('retire_missing', String(options.retireMissing));
    if (options?.toolSystem != null) searchParams.set('tool_system', options.toolSystem);
    const query = searchParams.toString();

    const { data } = await apiClient.post<ToolImportReport>(
      query ? `/assemblies/load-xml?${query}` : '/assemblies/load-xml',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: IMPORT_TIMEOUT_MS,
      }
    );
    return data;
  },
};

/**
 * Every active assembly, walked page by page until a short page ends the list.
 *
 * The tools table needs the whole set at once — it maps assemblies back onto the tools
 * of the current page — so there is no useful page size to hand through from the caller.
 */
export async function fetchAllActiveAssemblies(toolSystem?: ToolSystem | null): Promise<AssemblyModel[]> {
  const all: AssemblyModel[] = [];
  for (let page = 1; page <= ALL_ASSEMBLIES_MAX_PAGES; page += 1) {
    const batch = await assembliesApi.getAll({ active: true, page, size: ALL_ASSEMBLIES_PAGE_SIZE, toolSystem });
    all.push(...batch);
    if (batch.length < ALL_ASSEMBLIES_PAGE_SIZE) break;
  }
  return all;
}
